/** Phase 1: keep localStorage for Bearer fallback (used when cookie auth 401s).
 *  Phase 2: remove localStorage entirely — rely solely on httpOnly cookies. */
const SESSION_KEY = 'kraphene_session';
const ACTIVE_ORG_UNIT_KEY = 'kraphene_active_org_unit_id';

export interface Session {
  accessToken: string;
  refreshToken: string;
  requiresPasswordReset: boolean;
  user: {
    id: string;
    email: string;
    type: string;
    status: string;
    name?: string;
    image?: string | null;
    orgUnitId?: string | null;
  };
  permissions: string[];
}

export function getActiveOrgUnitId(): string | null {
  return localStorage.getItem(ACTIVE_ORG_UNIT_KEY);
}

export function setActiveOrgUnitId(id: string | null): void {
  if (id) {
    localStorage.setItem(ACTIVE_ORG_UNIT_KEY, id);
  } else {
    localStorage.removeItem(ACTIVE_ORG_UNIT_KEY);
  }
}

export function getSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch (err) {
    // Corrupted session data in localStorage — treat as logged out rather
    // than throwing, but log so we can tell "no session" from "bad session".
    console.error('Failed to parse stored session', err);
    return null;
  }
}

export function setSession(session: Session): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
}

export function getAccessToken(): string | null {
  return getSession()?.accessToken ?? null;
}

export function getRefreshToken(): string | null {
  return getSession()?.refreshToken ?? null;
}

export function updateTokens(accessToken: string, refreshToken: string): void {
  const session = getSession();
  if (!session) return;
  setSession({ ...session, accessToken, refreshToken });
}
